import React, { useEffect, useState } from "react";
import { db } from "../firebase"; // Adjust the import path as needed
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import AOS from "aos";
import "aos/dist/aos.css";

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

const InfoSection = () => {
  const [contributors, setContributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 1000, once: false });
    AOS.refresh();
  }, []);

  useEffect(() => {
    fetchContributors();
  }, []);

  const fetchContributors = async () => {
    setLoading(true);
    try {
      const contributorsRef = collection(db, "contributors");
      const q = query(contributorsRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      
      const contributorsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Group contributors by their group
      const groupedContributors = groupContributorsByCategory(contributorsData);
      setContributors(groupedContributors);
      setError(null);
    } catch (err) {
      console.error("Error fetching contributors:", err);
      setError("Failed to load contributors. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const groupContributorsByCategory = (contributorsList) => {
    // Define the order of groups
    const groupOrder = [
      "VISIONARY LEADERS",
      "STRATEGIC MINDS",
      "THE ARCHITECTS",
      "INNOVATION COMMANDERS",
      "THE POWER CIRCLE",
      "THE BRAINTRUST",
      "RISING TALENT",
      "YOUNG ACHIEVERS",
    ];

    // Create a map to group contributors
    const grouped = {};

    // Initialize groups
    groupOrder.forEach((group) => {
      grouped[group] = [];
    });

    // Add contributors to their respective groups
    contributorsList.forEach((contributor) => {
      if (contributor.group && grouped[contributor.group]) {
        grouped[contributor.group].push(contributor);
      } else if (contributor.group) {
        // If group exists but not in our order, add it as a new group
        if (!grouped[contributor.group]) {
          grouped[contributor.group] = [];
        }
        grouped[contributor.group].push(contributor);
      }
    });

    // Convert to array format for rendering
    const groupedArray = Object.keys(grouped)
      .filter((group) => grouped[group].length > 0) // Only show groups with members
      .map((group) => ({
        groupTitle: group,
        members: grouped[group],
      }));

    // Sort groups according to predefined order
    groupedArray.sort((a, b) => {
      const indexA = groupOrder.indexOf(a.groupTitle);
      const indexB = groupOrder.indexOf(b.groupTitle);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return groupedArray;
  };

  if (loading) {
    return (
      <section className="ceo-&-Main padding-container">
        <div className="info-section">
          <div className="page-wrapper">
            <div className="info">
              <div className="info-heading">
                <h1>
                  OUR MAIN <span className="orange-text">CONTRIBUTORS</span>
                </h1>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #FF8000",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="ceo-&-Main padding-container">
        <div className="info-section">
          <div className="page-wrapper">
            <div className="info">
              <div className="info-heading">
                <h1>
                  OUR MAIN <span className="orange-text">CONTRIBUTORS</span>
                </h1>
              </div>
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#e53e3e",
                }}
              >
                <p>{error}</p>
                <button
                  onClick={fetchContributors}
                  style={{
                    marginTop: "20px",
                    padding: "10px 20px",
                    backgroundColor: "#FF8000",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="ceo-&-Main padding-container"
      data-aos="fade-up"
      data-aos-duration="1000"
    >
      <div className="info-section">
        <div className="page-wrapper">
          <div className="info">
            <div className="info-heading">
              <h1>
                OUR MAIN <span className="orange-text">CONTRIBUTORS</span>
              </h1>
            </div>

            {contributors.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "#666",
                }}
              >
                <p>No contributors added yet. Check back soon!</p>
              </div>
            ) : (
              contributors.map((group, groupIndex) => (
                <div
                  key={groupIndex}
                  className="contributors-group"
                  style={{ marginTop: "50px", marginBottom: "50px" }}
                >
                  <div
                    className="contributors-group-title"
                    data-aos="fade-up"
                    data-aos-delay={groupIndex * 50}
                    style={{ textAlign: "center", marginBottom: "30px" }}
                  >
                    <h2
                      style={{
                        fontSize: "1.8rem",
                        color: "var(--orange-color)",
                        textTransform: "uppercase",
                        letterSpacing: "2px",
                      }}
                    >
                      {group.groupTitle}
                    </h2>
                  </div>

                  <div
                    className="Info-Grid-3 contributors"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: "20px",
                    }}
                  >
                    {group.members.map((person, index) => (
                      <div
                        className="info-data"
                        key={person.id || index}
                        data-aos="fade-up"
                        data-aos-delay={index * 100}
                        style={{ flex: "0 0 auto", width: "350px" }}
                      >
                        <div
                          className="info-img"
                          style={{
                            backgroundImage: `url(${
                              person.image
                                ? person.image
                                : "/assets/Images/default-user.png"
                            })`,
                            backgroundSize: "cover",
                            backgroundPosition: "top center",
                            backgroundRepeat: "no-repeat",
                          }}
                        ></div>

                        <div
                          className="info-text"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          <div className="text-inner" style={{ width: "100%" }}>
                            <h1 style={{ textTransform: "uppercase" }}>
                              {person.name}
                            </h1>

                            {/* Role line */}
                            <p style={{ margin: 0 }}>{person.role}</p>

                            {/* Designation line (rendered simply if exists) */}
                            {person.designation && (
                              <p style={{ margin: 0 }}>{person.designation}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InfoSection;